import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faUpload, 
  faDownload, 
  faCheckCircle, 
  faExclamationTriangle,
  faSpinner,
  faFileCode,
  faRobot,
  faEye,
  faCode
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { workflowAPI } from '../../utils/api';

const YamlWorkflowEditor = ({ 
  initialYaml = '', 
  workflowId = null, 
  onSave = null, 
  onCancel = null 
}) => {
  const [yamlContent, setYamlContent] = useState(initialYaml);
  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Default AI workflow template
  const defaultTemplate = `# AI Workflow Template
name: "My AI Workflow"
description: "Enter workflow description here"
version: "1.0.0"

# Global Configuration
config:
  timeout: 3600
  error_handling: "continue"
  logging: "detailed"

# AI Steps Only (for now)
steps:
  my_ai_step:
    name: "AI Step Name"
    type: "AI"
    description: "What this AI step does"
    config:
      provider: "openai"        # openai | anthropic | google
      api: "responses"          # responses | chat | assistants  
      model: "gpt-4"
      temperature: 0.7
      max_tokens: 500
      timeout: 30
      prompt_template: |
        Enter your AI prompt template here.
        Use {{input.variable_name}} for dynamic values.
    actions:
      success: "end"
      error: "error"

# Execution Flow
flow:
  start: "my_ai_step"
  error_handler: "error"
  end: "my_ai_step"`;

  // Initialize with template if empty
  useEffect(() => {
    if (!yamlContent.trim()) {
      setYamlContent(defaultTemplate);
    }
  }, []);

  // Validate YAML on content change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (yamlContent.trim()) {
        validateYaml();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [yamlContent]);

  const validateYaml = async () => {
    if (!yamlContent.trim()) return;

    setIsValidating(true);
    try {
      const response = await workflowAPI.validateYaml({ workflow_yaml: yamlContent });
      setValidation(response.validation);
    } catch (error) {
      console.error('Validation error:', error);
      setValidation({
        valid: false,
        errors: ['Failed to validate YAML: ' + error.message],
        warnings: []
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    if (!validation.valid) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (workflowId) {
        // Update existing workflow
        response = await workflowAPI.updateWorkflow(workflowId, {
          workflow_yaml: yamlContent
        });
      } else {
        // Create new workflow
        response = await workflowAPI.createFromYaml({ workflow_yaml: yamlContent });
      }

      toast.success('AI workflow saved successfully!');
      if (onSave) onSave(response);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save workflow: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.yaml') || file.name.endsWith('.yml')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setYamlContent(e.target.result);
        toast.success('YAML file loaded successfully');
      };
      reader.readAsText(file);
    } else {
      toast.error('Please select a .yaml or .yml file');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'workflow.yaml';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('YAML downloaded');
  };

  const insertTemplate = (template) => {
    const templates = {
      'simple': `
  new_ai_step:
    name: "New AI Step"
    type: "AI"
    config:
      provider: "openai"
      api: "responses"
      model: "gpt-4"
      prompt_template: "Enter your prompt here"
    actions:
      success: "end"
      error: "error"`,
      'analysis': `
  analyze_content:
    name: "Analyze Content"  
    type: "AI"
    config:
      provider: "openai"
      api: "responses"
      temperature: 0.2
      prompt_template: |
        Analyze the following content and provide insights:
        {{input.content}}
        
        Focus on: tone, key topics, actionable items
    actions:
      success: "end"
      error: "error"`
    };

    const templateContent = templates[template] || templates['simple'];
    setYamlContent(prev => prev + '\n' + templateContent);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faFileCode} className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                AI Workflow YAML Editor
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Create and edit AI-powered workflows using YAML
              </p>
            </div>
          </div>

          {/* Validation Status */}
          <div className="flex items-center space-x-2">
            {isValidating ? (
              <div className="flex items-center space-x-2 text-blue-600">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                <span className="text-sm">Validating...</span>
              </div>
            ) : validation.valid ? (
              <div className="flex items-center space-x-2 text-green-600">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span className="text-sm">Valid YAML</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-red-600">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span className="text-sm">{validation.errors.length} Error(s)</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 mt-4">
          <button
            onClick={handleSave}
            disabled={!validation.valid || isSaving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} className={isSaving ? 'animate-spin' : ''} />
            <span>{isSaving ? 'Saving...' : 'Save Workflow'}</span>
          </button>

          <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center space-x-2">
            <FontAwesomeIcon icon={faUpload} />
            <span>Upload YAML</span>
            <input
              type="file"
              accept=".yaml,.yml"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>

          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>Download</span>
          </button>

          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={showPreview ? faCode : faEye} />
            <span>{showPreview ? 'Edit' : 'Preview'}</span>
          </button>

          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="flex">
        {/* YAML Editor */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} p-6`}>
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faRobot} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                AI Step Templates:
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => insertTemplate('simple')}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
              >
                Simple AI Step
              </button>
              <button
                onClick={() => insertTemplate('analysis')}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200"
              >
                Analysis Step
              </button>
            </div>
          </div>

          <textarea
            value={yamlContent}
            onChange={(e) => setYamlContent(e.target.value)}
            className="w-full h-96 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your AI workflow YAML here..."
            spellCheck={false}
          />

          {/* Validation Errors */}
          {validation.errors.length > 0 && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                <span className="font-semibold text-red-800 dark:text-red-200">Validation Errors:</span>
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index} className="text-red-700 dark:text-red-300 text-sm">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Validation Warnings */}
          {validation.warnings.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                <span className="font-semibold text-yellow-800 dark:text-yellow-200">Warnings:</span>
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {validation.warnings.map((warning, index) => (
                  <li key={index} className="text-yellow-700 dark:text-yellow-300 text-sm">{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Preview Panel */}
        {showPreview && validation.valid && validation.parsed_workflow && (
          <div className="w-1/2 p-6 border-l border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Workflow Preview</h3>
            
            <div className="space-y-4">
              {/* Workflow Info */}
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {validation.parsed_workflow.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {validation.parsed_workflow.description}
                </p>
              </div>

              {/* Steps */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">AI Steps:</h4>
                <div className="space-y-2">
                  {validation.parsed_workflow.steps && Object.entries(validation.parsed_workflow.steps).map(([stepName, stepConfig]) => (
                    <div key={stepName} className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <FontAwesomeIcon icon={faRobot} className="text-blue-600" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">{stepName}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {stepConfig.config?.provider} • {stepConfig.config?.model}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YamlWorkflowEditor;